/**
 * Product best videos
 *
 * A row of vertical videos that play in place. One video plays at a time: starting one pauses the rest, and scrolling a
 * playing video out of view pauses it too, so a product page never has sound coming from something off screen.
 *
 * Each card carries its own play/pause button and a timeline that can be clicked or dragged to scrub. The bar under the
 * row shows how far along the row is scrolled.
 */
(function () {
  if (customElements.get('best-videos')) return;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class BestVideos extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-best-videos-track]');
      if (!this.track || this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.cards = Array.from(this.querySelectorAll('[data-best-videos-card]'));
      this.scrollbar = this.querySelector('[data-best-videos-scrollbar]');

      /* Videos play below full volume so a product page is never louder than the rest of the site. */
      this.volume = clamp((Number(this.getAttribute('volume')) || 70) / 100, 0, 1);

      this.cards.forEach((card) => this.setupCard(card));

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.onScroll();

      this.setupDragScroll();
      this.setupScrollbarDrag();

      /* A video that scrolls out of the viewport stops playing. */
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) {
              const video = entry.target.querySelector('[data-best-videos-video]');
              if (video && !video.paused) video.pause();
            }
          });
        }, { threshold: 0.35 });

        this.cards.forEach((card) => this.observer.observe(card));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onScroll);
      this.observer?.disconnect();
    }

    /**
     * Touch devices already swipe the row natively. This adds the same gesture for a mouse: press anywhere on the row
     * and drag. A drag that actually moved swallows the click that follows, so dragging past a video never plays it.
     */
    setupDragScroll() {
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let moved = 0;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        if (event.target.closest('[data-best-videos-timeline]')) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
        moved = 0;
        this.dragged = false;
        this.track.classList.add('is-dragging');
      });

      this.track.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;

        const distance = event.clientX - startX;
        moved = Math.max(moved, Math.abs(distance));

        /* Below this the press is still a click on a video, not a drag of the row. */
        if (moved < 5) return;

        if (!this.dragged) {
          this.dragged = true;
          /* Snapping fights a drag in progress; it is restored as soon as the drag ends. */
          this.track.style.scrollSnapType = 'none';
          this.track.setPointerCapture(event.pointerId);
        }

        event.preventDefault();
        this.track.scrollLeft = startScroll - distance;
      });

      const endDrag = (event) => {
        if (pointerId !== event.pointerId) return;
        if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);

        pointerId = null;
        this.track.classList.remove('is-dragging');
        this.track.style.scrollSnapType = '';

        /* Clear on the next frame: the click event fires right after pointerup. */
        requestAnimationFrame(() => { this.dragged = false; });
      };

      this.track.addEventListener('pointerup', endDrag);
      this.track.addEventListener('pointercancel', endDrag);

      this.track.addEventListener('click', (event) => {
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    /** The bar under the row doubles as a scrollbar: click or drag it to move through the videos. */
    setupScrollbarDrag() {
      const bar = this.scrollbar?.parentElement;
      if (!bar) return;

      const scrollToPointer = (clientX) => {
        const rect = bar.getBoundingClientRect();
        const thumbWidth = this.scrollbar.offsetWidth;
        const travel = rect.width - thumbWidth;
        if (travel <= 0) return;

        const ratio = clamp((clientX - rect.left - thumbWidth / 2) / travel, 0, 1);
        this.track.scrollLeft = ratio * (this.track.scrollWidth - this.track.clientWidth);
      };

      bar.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        bar.setPointerCapture(event.pointerId);
        this.scrollbarPointer = event.pointerId;
        scrollToPointer(event.clientX);
      });

      bar.addEventListener('pointermove', (event) => {
        if (this.scrollbarPointer !== event.pointerId) return;
        scrollToPointer(event.clientX);
      });

      const endBarDrag = (event) => {
        if (this.scrollbarPointer !== event.pointerId) return;
        if (bar.hasPointerCapture?.(event.pointerId)) bar.releasePointerCapture(event.pointerId);
        this.scrollbarPointer = null;
      };

      bar.addEventListener('pointerup', endBarDrag);
      bar.addEventListener('pointercancel', endBarDrag);
    }

    setupCard(card) {
      const video = card.querySelector('[data-best-videos-video]');
      if (!video) return;

      video.volume = this.volume;

      const playButton = card.querySelector('[data-best-videos-play]');
      const toggle = card.querySelector('[data-best-videos-toggle]');
      const sound = card.querySelector('[data-best-videos-sound]');
      const volumeInput = card.querySelector('[data-best-videos-volume]');
      const timeline = card.querySelector('[data-best-videos-timeline]');
      const progress = card.querySelector('[data-best-videos-progress]');

      const togglePlayback = () => {
        if (video.paused) {
          this.pauseOthers(video);
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      };

      playButton?.addEventListener('click', togglePlayback);
      toggle?.addEventListener('click', togglePlayback);
      video.addEventListener('click', togglePlayback);

      /* The sound button and the slider both read their state off the video, so they can never disagree. */
      sound?.addEventListener('click', () => {
        video.muted = !video.muted;
        /* Unmuting a video whose volume was dragged to zero has to give it something to play. */
        if (!video.muted && video.volume === 0) video.volume = this.volume;
      });

      volumeInput?.addEventListener('input', () => {
        const level = clamp(Number(volumeInput.value) / 100, 0, 1);
        video.volume = level;
        video.muted = level === 0;
      });

      const renderVolume = () => {
        if (!volumeInput) return;
        const level = video.muted ? 0 : video.volume;
        volumeInput.value = String(Math.round(level * 100));
        volumeInput.style.setProperty('--best-videos-volume', (level * 100).toFixed(0) + '%');
      };

      video.addEventListener('volumechange', () => {
        sound?.setAttribute('aria-pressed', String(video.muted));
        renderVolume();
      });

      renderVolume();

      video.addEventListener('play', () => {
        this.pauseOthers(video);
        card.classList.add('is-playing');
        card.classList.remove('is-paused');
      });

      video.addEventListener('pause', () => {
        card.classList.remove('is-playing');
        /* Keep the controls visible after a pause, so the timeline stays reachable. */
        if (video.currentTime > 0 && !video.ended) card.classList.add('is-paused');
      });

      video.addEventListener('ended', () => {
        card.classList.remove('is-playing', 'is-paused');
        if (progress) progress.style.width = '0%';
      });

      video.addEventListener('timeupdate', () => {
        if (this.scrubbing === timeline) return;
        this.renderProgress(video, timeline, progress);
      });

      if (timeline) this.setupTimeline(video, timeline, progress);
    }

    setupTimeline(video, timeline, progress) {
      const seekTo = (clientX) => {
        const rect = timeline.getBoundingClientRect();
        if (!rect.width || !isFinite(video.duration)) return;

        const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
        video.currentTime = ratio * video.duration;
        this.renderProgress(video, timeline, progress, ratio);
      };

      timeline.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        this.scrubbing = timeline;
        timeline.setPointerCapture(event.pointerId);
        seekTo(event.clientX);
      });

      timeline.addEventListener('pointermove', (event) => {
        if (this.scrubbing !== timeline) return;
        seekTo(event.clientX);
      });

      const endScrub = (event) => {
        if (this.scrubbing !== timeline) return;
        this.scrubbing = null;
        if (timeline.hasPointerCapture?.(event.pointerId)) timeline.releasePointerCapture(event.pointerId);
      };

      timeline.addEventListener('pointerup', endScrub);
      timeline.addEventListener('pointercancel', endScrub);

      timeline.addEventListener('keydown', (event) => {
        if (!isFinite(video.duration)) return;

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          video.currentTime = Math.max(0, video.currentTime - 5);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          video.currentTime = Math.min(video.duration, video.currentTime + 5);
        } else if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault();
          if (video.paused) {
            this.pauseOthers(video);
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }
      });
    }

    renderProgress(video, timeline, progress, forcedRatio) {
      const ratio = forcedRatio !== undefined
        ? forcedRatio
        : (isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0);

      if (progress) progress.style.width = (ratio * 100).toFixed(2) + '%';
      if (timeline) timeline.setAttribute('aria-valuenow', Math.round(ratio * 100));
    }

    pauseOthers(current) {
      this.cards.forEach((card) => {
        const video = card.querySelector('[data-best-videos-video]');
        if (video && video !== current && !video.paused) video.pause();
      });
    }

    onScroll() {
      const scrollable = this.track.scrollWidth - this.track.clientWidth;
      this.classList.toggle('best-videos--no-scroll', scrollable <= 1);
      if (!this.scrollbar || scrollable <= 1) return;

      const visibleRatio = this.track.clientWidth / this.track.scrollWidth;
      const progress = clamp(this.track.scrollLeft / scrollable, 0, 1);

      this.scrollbar.style.width = (visibleRatio * 100).toFixed(2) + '%';
      this.scrollbar.style.left = (progress * (100 - visibleRatio * 100)).toFixed(2) + '%';
    }
  }

  customElements.define('best-videos', BestVideos);
})();
